import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  RefreshCw, Plus, Search, Calendar, DollarSign, Play, Pause,
  MoreVertical, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const RecurringInvoices = () => {
  const { token, loading: authLoading } = useAuth();
  const [recurring, setRecurring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_address: '',
    frequency: 'monthly',
    start_date: '',
    end_date: '',
    notes: '',
    auto_send: false,
    line_items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 0, amount: 0 }]
  });

  useEffect(() => {
    if (!authLoading && token) {
      fetchRecurring();
    } else if (!authLoading && !token) {
      setLoading(false);
    }
  }, [token, authLoading]);

  const fetchRecurring = async () => {
    try {
      const response = await axios.get(`${API}/recurring-invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecurring(response.data);
    } catch (error) {
      toast.error('Failed to fetch recurring invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/recurring-invoices`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Recurring invoice created');
      setIsDialogOpen(false);
      resetForm();
      fetchRecurring();
    } catch (error) {
      toast.error('Error creating recurring invoice');
    }
  };

  const handleGenerate = async (recurringId) => {
    try {
      await axios.post(`${API}/recurring-invoices/${recurringId}/generate`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice generated');
      fetchRecurring();
    } catch (error) {
      toast.error('Error generating invoice');
    }
  };

  const handleToggle = async (recurringId) => {
    try {
      await axios.patch(`${API}/recurring-invoices/${recurringId}/toggle`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      fetchRecurring();
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_address: '',
      frequency: 'monthly',
      start_date: '',
      end_date: '',
      notes: '',
      auto_send: false,
      line_items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 0, amount: 0 }]
    });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: 0, tax_rate: 0, amount: 0 }]
    });
  };

  const updateLineItem = (index, field, value) => {
    const items = [...formData.line_items];
    items[index][field] = value;
    items[index].amount = items[index].quantity * items[index].unit_price * (1 + items[index].tax_rate / 100);
    setFormData({ ...formData, line_items: items });
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      daily: 'Daily',
      weekly: 'Weekly',
      biweekly: 'Bi-weekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly'
    };
    return labels[freq] || freq;
  };

  const activeRecurring = recurring.filter(r => r.is_active).length;
  const monthlyValue = recurring.filter(r => r.is_active && r.frequency === 'monthly').reduce((sum, r) => sum + r.total, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Recurring Invoices</h1>
          <p className="text-slate-400">Automate invoice generation</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              New Recurring
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Create Recurring Invoice</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Customer Name *</Label>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Customer Email *</Label>
                  <Input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-300">Customer Address</Label>
                  <Input
                    value={formData.customer_address}
                    onChange={(e) => setFormData({...formData, customer_address: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Frequency *</Label>
                  <Select 
                    value={formData.frequency}
                    onValueChange={(value) => setFormData({...formData, frequency: value})}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="daily" className="text-white">Daily</SelectItem>
                      <SelectItem value="weekly" className="text-white">Weekly</SelectItem>
                      <SelectItem value="biweekly" className="text-white">Bi-weekly</SelectItem>
                      <SelectItem value="monthly" className="text-white">Monthly</SelectItem>
                      <SelectItem value="quarterly" className="text-white">Quarterly</SelectItem>
                      <SelectItem value="yearly" className="text-white">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Line Items</Label>
                <div className="space-y-2 mt-2">
                  {formData.line_items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        className="col-span-4 bg-slate-800 border-slate-700 text-white"
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="col-span-2 bg-slate-800 border-slate-700 text-white"
                      />
                      <Input
                        type="number"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        className="col-span-2 bg-slate-800 border-slate-700 text-white"
                      />
                      <Input
                        type="number"
                        placeholder="Tax %"
                        value={item.tax_rate}
                        onChange={(e) => updateLineItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                        className="col-span-2 bg-slate-800 border-slate-700 text-white"
                      />
                      <div className="col-span-2 text-white text-sm">
                        MYR {item.amount.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="mt-2">
                  + Add Line
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.auto_send}
                  onChange={(e) => setFormData({...formData, auto_send: e.target.checked})}
                  className="rounded border-slate-700"
                />
                <Label className="text-slate-300">Auto-send to customer</Label>
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-slate-700">
                <div className="text-lg font-semibold text-white">
                  Per Invoice: MYR {formData.line_items.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                    Create
                  </Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <RefreshCw className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Recurring</p>
                <p className="text-xl font-bold text-white">{recurring.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <Play className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Active</p>
                <p className="text-xl font-bold text-white">{activeRecurring}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Monthly Revenue</p>
                <p className="text-xl font-bold text-white">MYR {monthlyValue.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="text-center py-10">
          <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : recurring.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-10 text-center">
            <RefreshCw className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">No recurring invoices</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {recurring.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${item.is_active ? 'bg-emerald-500/20' : 'bg-slate-500/20'}`}>
                        <RefreshCw className={`w-6 h-6 ${item.is_active ? 'text-emerald-400' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-white">{item.customer_name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${item.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
                            {item.is_active ? 'Active' : 'Paused'}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                          <span className="flex items-center gap-1">
                            <RefreshCw className="w-3 h-3" />
                            {getFrequencyLabel(item.frequency)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Next: {item.next_invoice_date}
                          </span>
                          <span>{item.invoices_generated} generated</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-white">
                          {item.currency} {item.total?.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-400">per invoice</p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4 text-slate-400" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-slate-800 border-slate-700">
                          <DropdownMenuItem 
                            className="text-slate-300"
                            onClick={() => handleGenerate(item.id)}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Generate Now
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-slate-300"
                            onClick={() => handleToggle(item.id)}
                          >
                            {item.is_active ? (
                              <><Pause className="w-4 h-4 mr-2" />Pause</>
                            ) : (
                              <><Play className="w-4 h-4 mr-2" />Resume</>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecurringInvoices;
